import LayeredAutocompleteSelect from "../../components/LayeredAutocompleteSelect";
/**
 * Observer "Usage Quality" tab — MSP/date filters, include-autoagent checkbox,
 * Report + Summary subtabs. ROOT-only via /api/observer. Always excludes internal users.
 *
 * Components / grep anchors:
 * - ObserverScopeFilters (MSP generate scope)
 * - viewMspFilter Select (client-side Report + Summary filter without re-generate)
 * - date fields, FormControlLabel checkbox, Generate / Force refresh buttons
 * - horizontally scrollable thread table: customer / Theo / productivity integration / user / time / actions
 * - Summary: Theo gaps + LLM gap narrative (Redis 30d by sorted thread ids; ticket chips;
 *   integration-aware so no-M365 customers are not treated as Theo M365 capability gaps)
 * - row click → Dialog detail popup; Sms / Security / Psychology action buttons
 * - MUI Tabs (Report | Summary), summary stats cards, Chip meta row
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import {
  Sms as SmsIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import ObserverApi, {
  TheoUsageGapNarrativeItem,
  TheoUsageGapNarrativeSections,
  TheoUsageQualityResponse,
  TheoUsageQualityThread,
} from '../apis/observerApi';
import ObserverScopeFilters from './ObserverScopeFilters';
import { useObserverScope } from '../hooks/useObserverScope';
import {
  ObserverThreadActionRow,
  observerThreadUserExperienceTitle,
  observerThreadUserExperienceUrl,
} from '../utils/observerThreadActionRow';
import { formatObserverTimestamp } from '../utils/threadUtils';

interface MSP {
  id: number;
  name: string;
}

/** Same handlers as Activity Report / AI Thread Observer action column. */
export interface UsageQualityThreadActionHandlers {
  onViewMessages: (thread: ObserverThreadActionRow) => void | Promise<void>;
  onViewAuditLogs: (thread: ObserverThreadActionRow) => void;
  onViewUsage?: (thread: ObserverThreadActionRow) => void | Promise<void>;
}

/** Hardcoded client-side max (server also enforces 30). */
const MAX_LOOKBACK_DAYS = 30;
const actionBtnSx = { minWidth: 'auto', px: 1 } as const;
const TABLE_MIN_WIDTH = 1900;
const EMPTY_GAP_SECTIONS: TheoUsageGapNarrativeSections = {
  gaps: [],
  missing_capabilities: [],
  enhancements: [],
  strengths_to_preserve: [],
};

const GAP_NARRATIVE_SECTION_META: Array<{
  key: keyof TheoUsageGapNarrativeSections;
  title: string;
}> = [
  { key: 'gaps', title: 'Gaps' },
  { key: 'missing_capabilities', title: 'Missing capabilities' },
  { key: 'enhancements', title: 'What to enhance' },
  { key: 'strengths_to_preserve', title: 'Strengths to preserve' },
];

function countGapNarrativeBullets(sections: TheoUsageGapNarrativeSections | null | undefined): number {
  if (!sections) return 0;
  return GAP_NARRATIVE_SECTION_META.reduce((n, s) => n + (sections[s.key]?.length || 0), 0);
}

/** Coerce string[] (legacy) or item objects into TheoUsageGapNarrativeItem[]. */
function coerceGapNarrativeItems(raw: unknown): TheoUsageGapNarrativeItem[] {
  if (!Array.isArray(raw)) return [];
  const out: TheoUsageGapNarrativeItem[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const summary = entry.trim();
      if (summary) out.push({ summary, ticket_ids: [], thread_ids: [] });
      continue;
    }
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const summary = String(row.summary ?? row.text ?? row.bullet ?? '').trim();
    if (!summary) continue;
    const ticket_ids = Array.isArray(row.ticket_ids)
      ? row.ticket_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const thread_ids = Array.isArray(row.thread_ids)
      ? row.thread_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
      : [];
    out.push({ summary, ticket_ids, thread_ids });
  }
  return out;
}

function supervisorTicketHref(ticketId: string): string {
  return `${window.location.origin}/supervisor/tickets/${encodeURIComponent(ticketId)}`;
}

function utcIsoDate(offsetDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function spanDays(start: string, end: string): number {
  const a = Date.parse(`${start}T00:00:00.000Z`);
  const b = Date.parse(`${end}T00:00:00.000Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.floor((b - a) / 86400000) + 1;
}

function helpTone(help: string): 'success' | 'warning' | 'error' | 'default' {
  if (help === 'helped') return 'success';
  if (help === 'partially_helped') return 'warning';
  if (help === 'not_helped') return 'error';
  return 'default';
}

/** Map a quality row onto Observer thread UX link helpers (chat / ticket / deep research). */
function qualityThreadToActionRow(t: TheoUsageQualityThread): ObserverThreadActionRow {
  const ticket =
    t.ticket_id != null && String(t.ticket_id).trim() !== '' ? String(t.ticket_id).trim() : null;
  return {
    ai_thread_id: t.ai_thread_id,
    thread_user_id: null,
    display_user_id: null,
    ticket_id: ticket,
    research_enabled: t.research_enabled,
    thread_type: ticket ? 'ticket_assistant' : 'chat_assistant',
    thread_name: t.thread_name,
    msp_id: t.msp_id,
    pinned: false,
    is_deleted: false,
    created_at: t.last_message_at || '',
    updated_at: t.last_message_at,
    message_count: 0,
    last_message_at: t.last_message_at,
    first_message_at: null,
  };
}

/** Display snake_case taxonomy keys as Title Case. */
function humanizeLabel(key: string | null | undefined): string {
  if (!key) return '—';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncateText(value: string | null | undefined, max = 120): string {
  const s = String(value || '').trim();
  if (!s) return '—';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function langsmithThreadUrl(aiThreadId: number): string {
  const projectId =
    process.env.REACT_APP_LANGSMITH_PROJECT_ID || '20f0ccc5-d50a-46f8-8f72-3e8470aa7a3c';
  return `https://smith.langchain.com/o/44975c4f-7463-4174-a1b2-7390e12b2622/projects/p/${projectId}/t/${aiThreadId}`;
}

function countLabels(
  threads: TheoUsageQualityThread[],
  keyFn: (t: TheoUsageQualityThread) => string | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of threads) {
    const k = keyFn(t) || 'unknown';
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

interface TheoUsageQualityPanelProps {
  msps: MSP[];
  threadActionHandlers?: UsageQualityThreadActionHandlers;
}

const TheoUsageQualityPanel: React.FC<TheoUsageQualityPanelProps> = ({
  msps,
  threadActionHandlers,
}) => {
  const api = useMemo(() => new ObserverApi(), []);
  const [startDateUtc, setStartDateUtc] = useState(() => utcIsoDate(-(7 - 1)));
  const [endDateUtc, setEndDateUtc] = useState(() => utcIsoDate());
  const [includeAutoagent, setIncludeAutoagent] = useState(false);
  const [subTab, setSubTab] = useState<'report' | 'summary'>('report');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TheoUsageQualityResponse | null>(null);
  /** Client-side filter applied to Report + Summary after generate. */
  const [viewMspId, setViewMspId] = useState<'all' | number>('all');
  const [detailThread, setDetailThread] = useState<TheoUsageQualityThread | null>(null);
  const [gapNarrativeSections, setGapNarrativeSections] =
    useState<TheoUsageGapNarrativeSections>(EMPTY_GAP_SECTIONS);
  const [gapNarrativeLoading, setGapNarrativeLoading] = useState(false);
  const [gapNarrativeError, setGapNarrativeError] = useState<string | null>(null);
  const gapNarrativeRequestIdRef = useRef(0);

  const {
    selectedMspIds,
    mspIdsForRequest,
    handleMspChange,
    handleUserChange,
    selectedUserIds,
  } = useObserverScope(msps, api, { fetchUsers: false });

  const loadReport = useCallback(async (forceRefresh = false) => {
    const start = startDateUtc.trim();
    const end = endDateUtc.trim();
    if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) {
      setError('Use UTC calendar dates in YYYY-MM-DD format.');
      return;
    }
    if (start > end) {
      setError('Start date must be on or before end date.');
      return;
    }
    const days = spanDays(start, end);
    if (days > MAX_LOOKBACK_DAYS) {
      setError(`Max lookback is ${MAX_LOOKBACK_DAYS} days (selected ${days}).`);
      return;
    }
    if (mspIdsForRequest.length === 0) {
      setError('Select at least one MSP.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.postTheoUsageQuality({
        startDate: start,
        endDate: end,
        mspIds: mspIdsForRequest,
        includeAutoagent,
        forceRefresh,
      });
      setData(res);
      if (mspIdsForRequest.length === 1) {
        setViewMspId(mspIdsForRequest[0]);
      } else {
        setViewMspId('all');
      }
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : 'Could not generate usage quality report.');
    } finally {
      setLoading(false);
    }
  }, [api, startDateUtc, endDateUtc, mspIdsForRequest, includeAutoagent]);

  const resultMsps = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of data?.report?.threads || []) {
      map.set(t.msp_id, t.msp_name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  useEffect(() => {
    if (viewMspId === 'all') return;
    if (!resultMsps.some((m) => m.id === viewMspId)) {
      setViewMspId('all');
    }
  }, [resultMsps, viewMspId]);

  const matchesViewMsp = useCallback(
    (t: TheoUsageQualityThread) => viewMspId === 'all' || t.msp_id === viewMspId,
    [viewMspId],
  );

  const interactiveThreads: TheoUsageQualityThread[] = useMemo(
    () => (data?.report?.threads || []).filter((t) => t.track === 'interactive' && matchesViewMsp(t)),
    [data, matchesViewMsp],
  );
  const autoagentThreads: TheoUsageQualityThread[] = useMemo(
    () => (data?.report?.threads || []).filter((t) => t.track === 'autoagent' && matchesViewMsp(t)),
    [data, matchesViewMsp],
  );

  const filteredHelpfulness = useMemo(
    () => countLabels(interactiveThreads, (t) => t.labels?.theo_helpfulness),
    [interactiveThreads],
  );
  const filteredIssues = useMemo(
    () => countLabels(interactiveThreads, (t) => t.labels?.primary_issue_type),
    [interactiveThreads],
  );
  const filteredGaps = useMemo(
    () =>
      countLabels(
        interactiveThreads.filter((t) => t.labels?.theo_helpfulness !== 'helped'),
        (t) => t.labels?.gap_category,
      ),
    [interactiveThreads],
  );

  /** Gap category → sample gap_detail strings for Summary “Theo gaps” section. */
  const gapSamplesByCategory = useMemo(() => {
    const out: Record<
      string,
      Array<{ ai_thread_id: number; gap_detail: string; theo_helpfulness: string }>
    > = {};
    for (const t of interactiveThreads) {
      if (t.labels?.theo_helpfulness === 'helped') continue;
      const cat = t.labels?.gap_category || 'unclear';
      if (!out[cat]) out[cat] = [];
      if (out[cat].length >= 3) continue;
      const detail = String(t.labels?.gap_detail || '').trim();
      out[cat].push({
        ai_thread_id: t.ai_thread_id,
        gap_detail: detail || '(no detail)',
        theo_helpfulness: t.labels?.theo_helpfulness || 'unclear',
      });
    }
    return out;
  }, [interactiveThreads]);

  const threadsWithGaps = useMemo(
    () => interactiveThreads.filter((t) => t.labels?.theo_helpfulness !== 'helped').length,
    [interactiveThreads],
  );

  const summaryThreadIdsKey = useMemo(
    () =>
      interactiveThreads
        .map((t) => t.ai_thread_id)
        .sort((a, b) => a - b)
        .join(','),
    [interactiveThreads],
  );

  const loadGapNarrative = useCallback(
    async (threads: TheoUsageQualityThread[], forceRefresh = false) => {
      const ids = threads
        .map((t) => t.ai_thread_id)
        .filter((id) => Number.isFinite(id))
        .sort((a, b) => a - b);
      if (!ids.length) {
        setGapNarrativeSections(EMPTY_GAP_SECTIONS);
        setGapNarrativeError(null);
        setGapNarrativeLoading(false);
        return;
      }
      const requestId = ++gapNarrativeRequestIdRef.current;
      setGapNarrativeLoading(true);
      setGapNarrativeError(null);
      try {
        const res = await api.postTheoUsageGapNarrative({
          forceRefresh,
          threads: threads.map((t) => ({
            ai_thread_id: t.ai_thread_id,
            ticket_id: t.ticket_id,
            msp_name: t.msp_name,
            customer_name: t.customer_name,
            customer_in_theo: Boolean(t.customer_in_theo),
            m365_integration: Boolean(t.m365_integration),
            gworkspace_integration: Boolean(t.gworkspace_integration),
            productivity_integration_label: t.productivity_integration_label,
            labels: {
              primary_issue_type: t.labels?.primary_issue_type,
              theo_helpfulness: t.labels?.theo_helpfulness,
              gap_category: t.labels?.gap_category,
              gap_detail: String(t.labels?.gap_detail || '').slice(0, 280),
              user_intent_summary: String(t.labels?.user_intent_summary || '').slice(0, 220),
              how_theo_helped: String(t.labels?.how_theo_helped || t.labels?.evidence || '').slice(
                0,
                280,
              ),
              features_used: (t.labels?.features_used || []).slice(0, 8),
            },
          })),
        });
        if (requestId !== gapNarrativeRequestIdRef.current) return;
        const nextSections: TheoUsageGapNarrativeSections = {
          gaps: coerceGapNarrativeItems(res?.sections?.gaps),
          missing_capabilities: coerceGapNarrativeItems(res?.sections?.missing_capabilities),
          enhancements: coerceGapNarrativeItems(res?.sections?.enhancements),
          strengths_to_preserve: coerceGapNarrativeItems(res?.sections?.strengths_to_preserve),
        };
        // Legacy flat bullets fallback if server returned only bullets
        if (
          countGapNarrativeBullets(nextSections) === 0 &&
          Array.isArray(res?.bullets) &&
          res.bullets.length
        ) {
          nextSections.gaps = coerceGapNarrativeItems(res.bullets);
        }
        setGapNarrativeSections(nextSections);
        if (countGapNarrativeBullets(nextSections) === 0) {
          setGapNarrativeError('LLM returned no gap bullets for this Summary view.');
        }
      } catch (e) {
        if (requestId !== gapNarrativeRequestIdRef.current) return;
        setGapNarrativeSections(EMPTY_GAP_SECTIONS);
        setGapNarrativeError(
          e instanceof Error ? e.message : 'Could not generate gap narrative.',
        );
      } finally {
        if (requestId === gapNarrativeRequestIdRef.current) {
          setGapNarrativeLoading(false);
        }
      }
    },
    [api],
  );

  useEffect(() => {
    if (subTab !== 'summary') return;
    if (!summaryThreadIdsKey) {
      setGapNarrativeSections(EMPTY_GAP_SECTIONS);
      setGapNarrativeError(null);
      setGapNarrativeLoading(false);
      return;
    }
    void loadGapNarrative(interactiveThreads, false);
    // summaryThreadIdsKey captures the filtered thread set; avoid depending on array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, summaryThreadIdsKey, loadGapNarrative]);

  const outcomePct = (count: number) =>
    interactiveThreads.length ? Math.round((100 * count) / interactiveThreads.length) : 0;
  const helpedPct = outcomePct(filteredHelpfulness.helped || 0);
  const partialPct = outcomePct(filteredHelpfulness.partially_helped || 0);
  const notHelpedPct = outcomePct(filteredHelpfulness.not_helped || 0);

  const viewMspLabel =
    viewMspId === 'all'
      ? 'All MSPs in result'
      : resultMsps.find((m) => m.id === viewMspId)?.name || `MSP ${viewMspId}`;

  const renderCustomerCell = (t: TheoUsageQualityThread) => {
    const integrationLabel =
      t.productivity_integration_label ||
      (t.m365_integration
        ? 'M365'
        : t.gworkspace_integration
          ? 'GWorkspace'
          : 'No integration');
    const hasIntegration = integrationLabel !== 'No integration';
    return (
      <Box sx={{ minWidth: 180 }}>
        <Typography variant="body2" fontWeight={500}>
          {t.customer_name || (t.customer_id != null ? `Customer ${t.customer_id}` : '—')}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
          <Chip
            size="small"
            label={t.customer_in_theo ? 'In Theo' : 'Not in Theo'}
            color={t.customer_in_theo ? 'success' : 'default'}
            variant={t.customer_in_theo ? 'filled' : 'outlined'}
          />
          <Chip
            size="small"
            label={integrationLabel}
            color={hasIntegration ? 'success' : 'default'}
            variant={hasIntegration ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>
    );
  };

  const renderActions = (t: TheoUsageQualityThread, stopRowClick = true) => {
    const actionRow = qualityThreadToActionRow(t);
    const handlers = threadActionHandlers;
    if (!handlers) return null;
    return (
      <Box
        sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}
        onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
      >
        <Button
          size="small"
          variant="outlined"
          sx={actionBtnSx}
          onClick={() => void handlers.onViewMessages(actionRow)}
          title="Read Messages"
        >
          <SmsIcon fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="outlined"
          sx={actionBtnSx}
          onClick={() => handlers.onViewAuditLogs(actionRow)}
          title="View Audit Logs"
        >
          <SecurityIcon fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="outlined"
          sx={actionBtnSx}
          onClick={() => window.open(langsmithThreadUrl(t.ai_thread_id), '_blank')}
          title="View in LangSmith"
        >
          <PsychologyIcon fontSize="small" />
        </Button>
      </Box>
    );
  };

  const renderCountTable = (title: string, rows: Record<string, number>, totalForPct?: number) => {
    const entries = Object.entries(rows).sort((a, b) => b[1] - a[1]);
    const showPct = typeof totalForPct === 'number' && totalForPct > 0;
    return (
      <Box sx={{ mb: 2, maxWidth: 560 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell width={64}>n</TableCell>
                {showPct ? <TableCell width={64}>%</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showPct ? 3 : 2}>—</TableCell>
                </TableRow>
              ) : (
                entries.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell>{humanizeLabel(k)}</TableCell>
                    <TableCell>{v}</TableCell>
                    {showPct ? (
                      <TableCell>{Math.round((100 * v) / totalForPct!)}%</TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderThreadTable = (threads: TheoUsageQualityThread[], title: string) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title} ({threads.length})
      </Typography>
      {threads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No threads in this track for the selected filters.
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflowX: 'auto', width: '100%' }}
        >
          <Table size="small" sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>MSP</TableCell>
                <TableCell sx={{ width: 190 }}>Thread</TableCell>
                <TableCell sx={{ width: 190 }}>Customer</TableCell>
                <TableCell sx={{ width: 160 }}>User</TableCell>
                <TableCell sx={{ width: 150 }}>Time</TableCell>
                <TableCell sx={{ width: 130 }}>Issue</TableCell>
                <TableCell sx={{ width: 120 }}>Helpfulness</TableCell>
                <TableCell sx={{ width: 200 }}>Intent</TableCell>
                <TableCell sx={{ width: 240 }}>How Theo helped</TableCell>
                <TableCell sx={{ width: 160 }}>Features</TableCell>
                <TableCell sx={{ width: 180 }}>Gap</TableCell>
                {threadActionHandlers ? <TableCell sx={{ width: 140 }}>Actions</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {threads.map((t) => {
                const lab = t.labels || ({} as TheoUsageQualityThread['labels']);
                const actionRow = qualityThreadToActionRow(t);
                const experienceUrl = observerThreadUserExperienceUrl(actionRow);
                const experienceTitle = observerThreadUserExperienceTitle(actionRow);
                // Ticket threads: show PSA ticket_id as the primary identity (not ai_thread_id).
                const ticketId =
                  t.ticket_id != null && String(t.ticket_id).trim() !== ''
                    ? String(t.ticket_id).trim()
                    : null;
                const threadPrimaryId = ticketId || String(t.ai_thread_id);
                return (
                  <TableRow
                    key={t.ai_thread_id}
                    hover
                    onClick={() => setDetailThread(t)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{t.msp_name}</TableCell>
                    <TableCell>
                      {experienceUrl ? (
                        <Link
                          href={experienceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          fontWeight={600}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                          title={
                            ticketId
                              ? `Ticket ${ticketId} (thread ${t.ai_thread_id})`
                              : experienceTitle
                          }
                        >
                          {threadPrimaryId}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {threadPrimaryId}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {t.thread_name || '—'}
                      </Typography>
                      {experienceUrl && (
                        <Typography variant="caption" display="block">
                          <Link
                            href={experienceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {experienceTitle}
                          </Link>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderCustomerCell(t)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.user_email || undefined}>
                        {t.user_email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {t.last_message_at ? formatObserverTimestamp(t.last_message_at) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{humanizeLabel(lab.primary_issue_type)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={humanizeLabel(lab.theo_helpfulness)}
                        color={helpTone(lab.theo_helpfulness)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" title={lab.user_intent_summary || undefined}>
                        {truncateText(lab.user_intent_summary, 100)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        title={lab.how_theo_helped || lab.evidence || undefined}
                      >
                        {truncateText(lab.how_theo_helped || lab.evidence, 140)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" title={(lab.features_used || []).join(', ')}>
                        {truncateText((lab.features_used || []).slice(0, 4).join(', '), 80)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lab.theo_helpfulness === 'helped' ? (
                        '—'
                      ) : (
                        <>
                          <Typography variant="caption" fontWeight={600} display="block">
                            {humanizeLabel(lab.gap_category)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={lab.gap_detail || undefined}
                          >
                            {truncateText(lab.gap_detail, 90)}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    {threadActionHandlers ? <TableCell>{renderActions(t)}</TableCell> : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const detailLab = detailThread?.labels;
  const detailActionRow = detailThread ? qualityThreadToActionRow(detailThread) : null;
  const detailExperienceUrl = detailActionRow
    ? observerThreadUserExperienceUrl(detailActionRow)
    : null;
  const detailExperienceTitle = detailActionRow
    ? observerThreadUserExperienceTitle(detailActionRow)
    : null;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Message-activity lookback (max {MAX_LOOKBACK_DAYS} days). Internal users always excluded.
        Thread labels are cached in Redis until last activity ages past {MAX_LOOKBACK_DAYS}d.
        Report table scrolls horizontally; click a row for full details.
      </Alert>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <ObserverScopeFilters
          msps={msps}
          users={[]}
          selectedMspIds={selectedMspIds}
          selectedUserIds={selectedUserIds}
          onMspChange={handleMspChange}
          onUserChange={handleUserChange}
          mspLabel="MSP scope"
          hideUserFilter
        />
        <Box sx={{ flex: '1 1 130px', minWidth: 0, maxWidth: { xs: '100%', lg: '16%' } }}>
          <TextField
            label="Start (UTC)"
            type="date"
            size="small"
            value={startDateUtc}
            onChange={(e) => setStartDateUtc(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ flex: '1 1 130px', minWidth: 0, maxWidth: { xs: '100%', lg: '16%' } }}>
          <TextField
            label="End (UTC)"
            type="date"
            size="small"
            value={endDateUtc}
            onChange={(e) => setEndDateUtc(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeAutoagent}
              onChange={(e) => setIncludeAutoagent(e.target.checked)}
              size="small"
            />
          }
          label="Include autoagent"
        />
        <Button variant="contained" onClick={() => loadReport(false)} disabled={loading}>
          Generate
        </Button>
        <Button variant="outlined" onClick={() => loadReport(true)} disabled={loading}>
          Force refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gap={2} sx={{ my: 3 }}>
          <CircularProgress size={28} />
          <Typography variant="body2">Generating quality report…</Typography>
        </Box>
      )}

      {data && !loading && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            <LayeredAutocompleteSelect
              fullWidth={false}
              label="View MSP"
              value={viewMspId === 'all' ? 'all' : String(viewMspId)}
              onChange={value => {
                  const v = value;
                  setViewMspId(v === 'all' ? 'all' : Number(v));
                }}
              size="small"
              sx={{ minWidth: 200 }}
              disableClearable
              options={[{ value: "all", label: "All MSPs in result" }, ...resultMsps.map(msp => ({ value: String(msp.id), label: msp.name }))]}
            />
            <Chip label={`${data.meta.threadCandidates} threads`} size="small" />
            <Chip
              label={`cached ${data.meta.cacheHits}`}
              size="small"
              color={data.meta.cacheHits > 0 ? 'success' : 'default'}
            />
            <Chip
              label={`labeled ${data.meta.cacheMisses}`}
              size="small"
              color={data.meta.cacheMisses > 0 ? 'warning' : 'default'}
            />
            <Chip
              label={data.meta.redisOk === false ? 'Redis off' : 'Redis ok'}
              size="small"
              color={data.meta.redisOk === false ? 'error' : 'default'}
              variant="outlined"
            />
            <Chip label={data.meta.model} size="small" variant="outlined" />
            <Chip
              label={`${data.report.startDateUtc} → ${data.report.endDateUtc}`}
              size="small"
              variant="outlined"
            />
            {data.meta.cache === 'report_hit' && (
              <Chip label="report cache" size="small" color="info" variant="outlined" />
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            {Object.entries(filteredHelpfulness).map(([k, v]) => (
              <Chip key={k} label={`${humanizeLabel(k)}: ${v}`} color={helpTone(k)} size="small" />
            ))}
          </Stack>

          <Tabs
            value={subTab}
            onChange={(_, v: 'report' | 'summary') => {
              setSubTab(v);
              // Show loading immediately when opening Summary (before effect fetch starts).
              if (v === 'summary' && interactiveThreads.length > 0) {
                setGapNarrativeLoading(true);
                setGapNarrativeError(null);
              }
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Report" value="report" />
            <Tab
              value="summary"
              label={
                gapNarrativeLoading && subTab === 'summary' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <span>Summary</span>
                  </Stack>
                ) : (
                  'Summary'
                )
              }
            />
          </Tabs>

          {subTab === 'report' ? (
            <Box>
              {renderThreadTable(interactiveThreads, 'Interactive')}
              {includeAutoagent && renderThreadTable(autoagentThreads, 'Autoagent')}
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              <Typography variant="h5" gutterBottom>
                Usage quality summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {data.report.startDateUtc} → {data.report.endDateUtc} UTC · {viewMspLabel} · internals
                excluded
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }} useFlexGap>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 110 }}>
                  <Typography variant="h5">{interactiveThreads.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Interactive
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 110 }}>
                  <Typography variant="h5">{helpedPct}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Helped
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 110 }}>
                  <Typography variant="h5">{partialPct}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Partial
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 110 }}>
                  <Typography variant="h5">{notHelpedPct}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Not helped
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 110 }}>
                  <Typography variant="h5">{threadsWithGaps}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    With gaps
                  </Typography>
                </Paper>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 860, bgcolor: 'background.paper' }}>
                {gapNarrativeLoading && (
                  <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1, flexWrap: 'wrap' }}
                  useFlexGap
                >
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
                    <Typography variant="h6" fontWeight={600}>
                      Gaps & capabilities (LLM)
                    </Typography>
                    {gapNarrativeLoading && (
                      <Chip size="small" color="info" label="Generating…" />
                    )}
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={gapNarrativeLoading || !interactiveThreads.length}
                    onClick={() => void loadGapNarrative(interactiveThreads, true)}
                    startIcon={
                      gapNarrativeLoading ? <CircularProgress size={14} color="inherit" /> : undefined
                    }
                  >
                    {gapNarrativeLoading ? 'Generating' : 'Regenerate'}
                  </Button>
                </Stack>
                {gapNarrativeLoading && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1.5}
                    sx={{ py: 3, px: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <CircularProgress size={36} />
                    <Typography variant="body1" fontWeight={500}>
                      Generating gap summary…
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Analyzing {interactiveThreads.length} threads in this view. First run may take
                      up to a couple of minutes; later views with the same threads are served from
                      Redis.
                    </Typography>
                  </Box>
                )}
                {!gapNarrativeLoading && gapNarrativeError && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void loadGapNarrative(interactiveThreads, true)}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {gapNarrativeError}
                  </Alert>
                )}
                {!gapNarrativeLoading &&
                  !gapNarrativeError &&
                  countGapNarrativeBullets(gapNarrativeSections) > 0 && (
                  <Stack spacing={2}>
                    {GAP_NARRATIVE_SECTION_META.map(({ key, title }) => {
                      const items = gapNarrativeSections[key] || [];
                      if (!items.length) return null;
                      return (
                        <Box key={key}>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {title}
                          </Typography>
                          {/* gap-narrative-section-list: summary + ticket chips */}
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {items.map((item, i) => {
                              const summary =
                                typeof item === 'string'
                                  ? item
                                  : String(item?.summary || '').trim();
                              const ticketIds =
                                typeof item === 'string'
                                  ? []
                                  : (item?.ticket_ids || []).filter(Boolean);
                              if (!summary) return null;
                              return (
                                <Box
                                  component="li"
                                  key={`${key}-${i}-${summary.slice(0, 24)}`}
                                  sx={{ mb: 1 }}
                                >
                                  <Typography variant="body2" sx={{ mb: ticketIds.length ? 0.5 : 0 }}>
                                    {summary}
                                  </Typography>
                                  {ticketIds.length > 0 && (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      useFlexGap
                                      flexWrap="wrap"
                                      sx={{ pl: 0 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ alignSelf: 'center', mr: 0.25 }}
                                      >
                                        Tickets:
                                      </Typography>
                                      {ticketIds.map((ticketId) => (
                                        <Chip
                                          key={`${key}-${i}-t-${ticketId}`}
                                          size="small"
                                          component="a"
                                          clickable
                                          href={supervisorTicketHref(ticketId)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          label={ticketId}
                                          sx={{ height: 22, borderRadius: 0.5 }}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                {!gapNarrativeLoading &&
                  !gapNarrativeError &&
                  countGapNarrativeBullets(gapNarrativeSections) === 0 &&
                  interactiveThreads.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No LLM summary yet. Click Regenerate if this stays empty.
                    </Typography>
                  )}
              </Paper>

              {renderCountTable('Helpfulness', filteredHelpfulness, interactiveThreads.length)}
              {renderCountTable('Issue types', filteredIssues, interactiveThreads.length)}

              <Box sx={{ mb: 3, maxWidth: 720 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Theo gaps
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Categories for partially helped / not helped threads ({threadsWithGaps} of{' '}
                  {interactiveThreads.length}).
                </Typography>
                {Object.keys(filteredGaps).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No gaps in this view (all helped, or no threads).
                  </Typography>
                ) : (
                  <>
                    {renderCountTable('Gap categories', filteredGaps, interactiveThreads.length)}
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      {Object.entries(filteredGaps)
                        .sort((a, b) => b[1] - a[1])
                        .map(([cat, n]) => (
                          <Paper key={cat} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {humanizeLabel(cat)}
                              </Typography>
                              <Chip size="small" label={`${n}`} />
                              <Typography variant="caption" color="text.secondary">
                                {interactiveThreads.length
                                  ? `${Math.round((100 * n) / interactiveThreads.length)}% of interactive`
                                  : ''}
                              </Typography>
                            </Stack>
                            {(gapSamplesByCategory[cat] || []).map((s) => (
                              <Typography
                                key={`${cat}-${s.ai_thread_id}`}
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5, pl: 1, borderLeft: 2, borderColor: 'divider' }}
                              >
                                <Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  onClick={() => {
                                    const row = interactiveThreads.find(
                                      (t) => t.ai_thread_id === s.ai_thread_id,
                                    );
                                    if (row) setDetailThread(row);
                                  }}
                                  sx={{ mr: 0.75, verticalAlign: 'baseline' }}
                                >
                                  #{s.ai_thread_id}
                                </Link>
                                ({humanizeLabel(s.theo_helpfulness)}) {s.gap_detail}
                              </Typography>
                            ))}
                          </Paper>
                        ))}
                    </Stack>
                  </>
                )}
              </Box>

              {includeAutoagent && autoagentThreads.length > 0 && (
                renderCountTable(
                  'Autoagent investigation quality',
                  countLabels(autoagentThreads, (t) => t.labels?.investigation_quality),
                )
              )}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={Boolean(detailThread)}
        onClose={() => setDetailThread(null)}
        maxWidth="md"
        fullWidth
      >
        {detailThread && detailLab && (
          <>
            <DialogTitle>
              {detailThread.ticket_id != null && String(detailThread.ticket_id).trim() !== ''
                ? `Ticket ${String(detailThread.ticket_id).trim()}`
                : `Thread ${detailThread.ai_thread_id}`}
              <Typography variant="body2" color="text.secondary">
                {detailThread.thread_name || '—'} · {detailThread.msp_name}
                {detailThread.ticket_id != null && String(detailThread.ticket_id).trim() !== ''
                  ? ` · AI thread ${detailThread.ai_thread_id}`
                  : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2">Customer</Typography>
                  {renderCustomerCell(detailThread)}
                </Box>
                <Box>
                  <Typography variant="subtitle2">User</Typography>
                  <Typography variant="body2">{detailThread.user_email || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Last activity</Typography>
                  <Typography variant="body2">
                    {detailThread.last_message_at
                      ? formatObserverTimestamp(detailThread.last_message_at)
                      : '—'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2">Issue</Typography>
                  <Typography variant="body2">
                    {humanizeLabel(detailLab.primary_issue_type)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Helpfulness</Typography>
                  <Chip
                    size="small"
                    label={humanizeLabel(detailLab.theo_helpfulness)}
                    color={helpTone(detailLab.theo_helpfulness)}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2">Intent</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailLab.user_intent_summary || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">How Theo helped</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailLab.how_theo_helped || detailLab.evidence || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Features</Typography>
                  <Typography variant="body2">
                    {(detailLab.features_used || []).join(', ') || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Gap</Typography>
                  {detailLab.theo_helpfulness === 'helped' ? (
                    <Typography variant="body2">—</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight={600}>
                        {humanizeLabel(detailLab.gap_category)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {detailLab.gap_detail || '—'}
                      </Typography>
                    </>
                  )}
                </Box>
                {detailLab.evidence && detailLab.how_theo_helped !== detailLab.evidence && (
                  <Box>
                    <Typography variant="subtitle2">Evidence</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detailLab.evidence}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {renderActions(detailThread, false)}
                {detailExperienceUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={detailExperienceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {detailExperienceTitle}
                  </Button>
                )}
              </Box>
              <Button onClick={() => setDetailThread(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TheoUsageQualityPanel;
